import { useEffect } from 'react';
import { Field, Form, useFormikContext } from 'formik';
import { Copy, ArrowRight, Eye } from 'lucide-react';

import { EnvironmentId } from '@/react/portainer/environments/types';
import { useEnvironment } from '@/react/portainer/environments/queries/useEnvironment';
import { isKubernetesEnvironment } from '@/react/portainer/environments/utils';

import { LoadingButton } from '@@/buttons/LoadingButton';
import { Input } from '@@/form-components/Input';
import { FormControl } from '@@/form-components/FormControl';
import { FormError } from '@@/form-components/FormError';
import { TextTip } from '@@/Tip/TextTip';
import { SwitchField } from '@@/form-components/SwitchField';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

import { StackType } from '../../../../../common/stacks/types';

import { FormSubmitValues, ActionType } from './StackDuplicationForm.types';
import { useValidation } from './StackDuplicationForm.validation';
import { EnvSelector } from './EnvSelector';

interface Props {
  yamlError?: string;
  currentEnvironmentId: EnvironmentId;
  currentStackName: string;
  stackType: number;
}

export function StackDuplicationFormInner({
  yamlError,
  currentEnvironmentId,
  currentStackName,
  stackType,
}: Props) {
  const { values, errors, setFieldValue, submitForm, isSubmitting } =
    useFormikContext<FormSubmitValues>();

  const validState = useValidation({
    values,
    currentStackName,
    currentEnvironmentId,
  });

  const isEnvSelected = !!values.environmentId;
  const selectedEnvQuery = useEnvironment(values.environmentId);
  const isKubernetesEnv =
    selectedEnvQuery.data &&
    isKubernetesEnvironment(selectedEnvQuery.data.Type);

  // Keep form state in sync so the parent can read it in handleMigrate
  const kubeFlag = !!isKubernetesEnv;
  useEffect(() => {
    setFieldValue('isKubernetesTarget', kubeFlag);
  }, [kubeFlag, setFieldValue]);

  // Only show conversion options for Docker Compose or Swarm stacks
  const canConvert =
    isKubernetesEnv &&
    (stackType === StackType.DockerCompose ||
      stackType === StackType.DockerSwarm);

  async function handleAction(type: ActionType) {
    // Set the actionType in form values before submitting
    await setFieldValue('actionType', type);
    await submitForm();
  }

  const isMigrateInProgress = isSubmitting && values.actionType === 'migrate';
  const isDuplicateInProgress =
    isSubmitting && values.actionType === 'duplicate';
  const isPreviewInProgress = isSubmitting && values.actionType === 'preview';

  const isMigrateDisabled = isSubmitting || !validState.migrate;
  const isDuplicateDisabled =
    isSubmitting || !validState.duplicate || !!yamlError;
  const isPreviewDisabled =
    isSubmitting || !canConvert || !values.environmentId || values.isHelm;

  return (
    <Form className="form-horizontal">
      <TextTip color="blue">
        <p>This feature allows you to duplicate or migrate this stack. </p>
        <p>To rename the stack, choose the same environment when migrating.</p>
      </TextTip>

      <div className="form-group">
        <div className="col-sm-12">
          <Field
            as={Input}
            type="text"
            placeholder="Stack name (optional for migration)"
            aria-label="Stack name"
            name="newName"
            data-cy="stack-duplicate-name-input"
          />
          {errors.newName && <FormError>{errors.newName}</FormError>}
        </div>
      </div>

      <EnvSelector
        onChange={(value) => setFieldValue('environmentId', value)}
        value={values.environmentId}
        error={errors.environmentId}
      />

      {isKubernetesEnv && (
        <>
          <TextTip color="orange" className="icon-orange mb-2 font-bold">
            This feature is currently experimental and is not recommended for
            production workloads.
          </TextTip>
          <FormControl
            label="Namespace"
            inputId="namespace"
            errors={errors.namespace}
            tooltip="If the specified namespace does not exist, it will be automatically created during migration"
          >
            <Field
              as={Input}
              type="text"
              name="namespace"
              id="namespace"
              placeholder="default"
              data-cy="stack-migrate-namespace-input"
            />
          </FormControl>
        </>
      )}

      {canConvert && (
        <div className="form-group">
          <div className="col-sm-12">
            <SwitchField
              label="Convert to Helm chart"
              checked={values.isHelm}
              onChange={(value) => setFieldValue('isHelm', value)}
              labelClass="col-sm-3 col-lg-2"
              data-cy="stack-convert-helm-toggle"
            />
          </div>
        </div>
      )}

      <div className="inline-flex gap-2">
        <LoadingButton
          type="button"
          color="primary"
          size="small"
          className="!ml-0"
          disabled={isMigrateDisabled}
          isLoading={isMigrateInProgress}
          loadingText={
            values.environmentId === currentEnvironmentId
              ? 'Renaming in progress...'
              : 'Migration in progress...'
          }
          onClick={() => handleAction('migrate')}
          icon={ArrowRight}
          data-cy="stack-migrate-button"
        >
          {values.environmentId === currentEnvironmentId ? 'Rename' : 'Migrate'}
        </LoadingButton>

        <LoadingButton
          type="button"
          color="primary"
          size="small"
          disabled={isDuplicateDisabled}
          isLoading={isDuplicateInProgress}
          loadingText="Duplication in progress..."
          onClick={() => handleAction('duplicate')}
          icon={Copy}
          data-cy="stack-duplicate-button"
        >
          Duplicate
        </LoadingButton>

        {canConvert &&
          (values.isHelm ? (
            <TooltipWithChildren message="Preview for Helm chart conversion is not supported by Kompose">
              <div className="inline-block">
                <LoadingButton
                  type="button"
                  color="primary"
                  size="small"
                  disabled
                  isLoading={isPreviewInProgress}
                  loadingText="Preview in progress..."
                  onClick={() => handleAction('preview')}
                  icon={Eye}
                  data-cy="stack-preview-button"
                >
                  Preview
                </LoadingButton>
              </div>
            </TooltipWithChildren>
          ) : (
            <LoadingButton
              type="button"
              color="primary"
              size="small"
              disabled={isPreviewDisabled}
              isLoading={isPreviewInProgress}
              loadingText="Preview in progress..."
              onClick={() => handleAction('preview')}
              icon={Eye}
              data-cy="stack-preview-button"
            >
              Preview
            </LoadingButton>
          ))}
      </div>

      {yamlError && isEnvSelected && (
        <div className="form-group" role="alert" aria-label="Yaml Error">
          <div>
            <span className="text-danger small">{yamlError}</span>
          </div>
        </div>
      )}
    </Form>
  );
}
